import { SHOPIFY_STORE_URL, SHOPIFY_STOREFRONT_ACCESS_TOKEN } from '../config';
import { productCache, collectionCache } from '../utils/cache';
import { debounceAsync } from '../utils/debounce';
import axios from 'axios';

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const cache = new Map();

class ShopifyService {
  constructor() {
    this.storeUrl = SHOPIFY_STORE_URL;
    this.accessToken = SHOPIFY_STOREFRONT_ACCESS_TOKEN;
    this.graphqlEndpoint = `https://${this.storeUrl}/api/2024-01/graphql.json`;
    this.backendUrl = import.meta.env.VITE_API_URL || 'http://localhost:5050';
    this.client = null;
    this.debouncedSearch = debounceAsync(this._searchProducts.bind(this), 300);
    this.debouncedFetch = debounceAsync(this._fetchProducts.bind(this), 300);
    this.initializeClient();
  }

  async initializeClient() {
    if (typeof window !== 'undefined' && window.ShopifyBuy) {
      try {
        this.client = await window.ShopifyBuy.buildClient({
          domain: this.storeUrl,
          storefrontAccessToken: this.accessToken,
          apiVersion: '2024-01'
        });
      } catch (error) {
        console.error('Failed to initialize Shopify client:', error);
      }
    }
  }

  async searchProducts(query, filters = {}, page = 1, limit = 20) {
    return this.debouncedSearch(query, filters, page, limit);
  }

  async _searchProducts(query, filters = {}, page = 1, limit = 20) {
    const cacheKey = `search:${query}:${JSON.stringify(filters)}:${page}:${limit}`;
    const cached = productCache.get(cacheKey);
    if (cached) return cached;

    try {
      let results;
      if (this.client) {
        results = await this.client.product.fetchQuery({
          query: this.buildProductQuery({ ...filters, query }),
          first: limit,
          after: page > 1 ? ((page - 1) * limit).toString() : null
        });
      } else {
        const response = await fetch(
          `${this.backendUrl}/api/shopify/products?q=${encodeURIComponent(query)}&page=${page}&limit=${limit}`
        );
        const data = await response.json();
        results = data.products;
      }

      const transformed = this.transformProducts(results);
      productCache.set(cacheKey, transformed);
      return transformed;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  async fetchProducts(filters = {}, page = 1, limit = 20) {
    return this.debouncedFetch(filters, page, limit);
  }

  async _fetchProducts(filters = {}, page = 1, limit = 20) {
    const cacheKey = `products:${JSON.stringify(filters)}:${page}:${limit}`;
    const cached = productCache.get(cacheKey);
    if (cached) return cached;

    if (this.client) {
      try {
        const products = await this.client.product.fetchQuery({
          query: this.buildProductQuery(filters),
          first: limit,
          after: page > 1 ? ((page - 1) * limit).toString() : null
        });
        const transformed = this.transformProducts(products);
        productCache.set(cacheKey, transformed);
        return transformed;
      } catch (error) {
        console.error('Error fetching products with client:', error);
      }
    }

    const query = `
      query Products($first: Int!, $after: String, $query: String) {
        products(first: $first, after: $after, query: $query) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              title
              description
              handle
              priceRange {
                minVariantPrice {
                  amount
                }
                maxVariantPrice {
                  amount
                }
              }
              images(first: 1) {
                edges {
                  node {
                    url
                    altText
                  }
                }
              }
              variants(first: 1) {
                edges {
                  node {
                    id
                    price {
                      amount
                    }
                  }
                }
              }
              collections(first: 5) {
                edges {
                  node {
                    title
                    handle
                  }
                }
              }
              tags
              vendor
              createdAt
              updatedAt
            }
          }
        }
      }
    `;

    const variables = {
      first: limit,
      after: page > 1 ? ((page - 1) * limit).toString() : null,
      query: this.buildProductQuery(filters)
    };

    try {
      const response = await fetch(this.graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query, variables })
      });

      const data = await response.json();
      const transformed = this.transformProducts(data.data.products.edges);
      productCache.set(cacheKey, transformed);
      return transformed;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  async fetchCollections() {
    const cacheKey = 'collections';
    const cached = collectionCache.get(cacheKey);
    if (cached) return cached;

    const query = `
      query Collections {
        collections(first: 20) {
          edges {
            node {
              id
              title
              handle
              description
              image {
                url
                altText
              }
              products(first: 1) {
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(this.graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query })
      });

      const data = await response.json();
      const collections = data.data.collections.edges.map(edge => edge.node);
      collectionCache.set(cacheKey, collections);
      return collections;
    } catch (error) {
      console.error('Error fetching collections:', error);
      throw error;
    }
  }

  async createCart() {
    const mutation = `
      mutation createCart {
        cartCreate {
          cart {
            id
            checkoutUrl
            lines(first: 0) {
              edges {
                node {
                  id
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    try {
      const response = await fetch(this.graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query: mutation })
      });

      const data = await response.json();
      return data.data.cartCreate.cart;
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error;
    }
  }

  async addToCart(cartId, variantId, quantity = 1) {
    const mutation = `
      mutation addToCart($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            id
            lines(first: 10) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      price {
                        amount
                      }
                      product {
                        title
                      }
                    }
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const variables = {
      cartId,
      lines: [
        {
          merchandiseId: variantId,
          quantity
        }
      ]
    };

    try {
      const response = await fetch(this.graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query: mutation, variables })
      });

      const data = await response.json();
      return data.data.cartLinesAdd.cart;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  buildProductQuery(filters) {
    const queryParts = [];
    
    if (filters.priceRange) {
      queryParts.push(`variants.price:>="${filters.priceRange[0]}" variants.price:<="${filters.priceRange[1]}"`);
    }
    
    if (filters.medium?.length) {
      queryParts.push(`tag:${filters.medium.join(' OR tag:')}`);
    }
    
    if (filters.style?.length) {
      queryParts.push(`tag:${filters.style.join(' OR tag:')}`);
    }
    
    if (filters.artist?.length) {
      queryParts.push(`vendor:${filters.artist.join(' OR vendor:')}`);
    }

    return queryParts.join(' AND ');
  }

  transformProducts(edges) {
    return edges.map(edge => {
      const product = edge.node;
      return {
        id: product.id,
        title: product.title,
        description: product.description,
        handle: product.handle,
        price: parseFloat(product.variants.edges[0].node.price.amount),
        image: product.images.edges[0]?.node.url || '',
        altText: product.images.edges[0]?.node.altText || '',
        variantId: product.variants.edges[0].node.id,
        collections: product.collections.edges.map(edge => edge.node),
        tags: product.tags,
        artist: product.vendor,
        createdAt: product.createdAt,
        updatedAt: product.updatedAt
      };
    });
  }



  /**
   * Get real shipping rates using Shopify Storefront API
   * Uses Cart API (Checkout API is deprecated)
   */
  async getRealShippingRates(shippingAddress, lineItems = []) {
    try {
      console.log('🚚 Getting REAL Shopify shipping rates via Storefront API (Cart API)...');
      
      // Create cart with line items
      const cart = await this.createCartWithItems(lineItems);
      
      if (!cart || !cart.id) {
        throw new Error('Failed to create cart for shipping rate calculation');
      }

      // Update cart with shipping address
      const updatedCart = await this.updateCartShippingAddress(cart.id, shippingAddress);

      // Get available shipping rates for the cart
      const shippingRates = await this.getCartShippingRates(updatedCart.id);
      
      console.log(`✅ Retrieved ${shippingRates.length} real Shopify shipping rates`);
      return shippingRates;
      
    } catch (error) {
      console.error('❌ Error getting real Storefront API shipping rates:', error);
      throw error;
    }
  }

  /**
   * Create a cart with line items (replaces deprecated checkout)
   */
  async createCartWithItems(lineItems = []) {
    const mutation = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
            lines(first: 10) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const input = {};

    // Add line items if we have them
    if (lineItems && lineItems.length > 0) {
      input.lines = lineItems.map(item => ({
        merchandiseId: item.variantId,
        quantity: item.quantity || 1
      }));
    }

    const variables = { input };

    try {
      const response = await fetch(this.graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query: mutation, variables })
      });

      const data = await response.json();
      
      if (data.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
      }

      if (data.data.cartCreate.userErrors.length > 0) {
        throw new Error(`Cart errors: ${JSON.stringify(data.data.cartCreate.userErrors)}`);
      }

      return data.data.cartCreate.cart;
    } catch (error) {
      console.error('Error creating cart:', error);
      throw error;
    }
  }

  /**
   * Update cart with shipping address
   */
  async updateCartShippingAddress(cartId, shippingAddress) {
    const mutation = `
      mutation cartBuyerIdentityUpdate($cartId: ID!, $buyerIdentity: CartBuyerIdentityInput!) {
        cartBuyerIdentityUpdate(cartId: $cartId, buyerIdentity: $buyerIdentity) {
          cart {
            id
            deliveryGroups(first: 1) {
              edges {
                node {
                  deliveryOptions {
                    handle
                    title
                    estimatedCost {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const buyerIdentity = {
      deliveryAddressPreferences: [{
        deliveryAddress: {
          address1: shippingAddress.address1,
          address2: shippingAddress.address2 || '',
          city: shippingAddress.city,
          province: shippingAddress.province || shippingAddress.state,
          zip: shippingAddress.zip || shippingAddress.postal_code,
          country: shippingAddress.country || 'US',
          firstName: shippingAddress.firstName || 'Customer',
          lastName: shippingAddress.lastName || 'Name'
        }
      }]
    };

    const variables = { cartId, buyerIdentity };

    try {
      const response = await fetch(this.graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query: mutation, variables })
      });

      const data = await response.json();
      
      if (data.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
      }

      if (data.data.cartBuyerIdentityUpdate.userErrors.length > 0) {
        throw new Error(`Cart update errors: ${JSON.stringify(data.data.cartBuyerIdentityUpdate.userErrors)}`);
      }

      return data.data.cartBuyerIdentityUpdate.cart;
    } catch (error) {
      console.error('Error updating cart shipping address:', error);
      throw error;
    }
  }

  /**
   * Get shipping rates for a cart
   */
  async getCartShippingRates(cartId) {
    const query = `
      query getCart($id: ID!) {
        cart(id: $id) {
          id
          deliveryGroups(first: 1) {
            edges {
              node {
                deliveryOptions {
                  handle
                  title
                  estimatedCost {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = { id: cartId };

    try {
      // Poll for shipping rates (they may not be immediately available)
      let attempts = 0;
      const maxAttempts = 10;
      
      while (attempts < maxAttempts) {
        const response = await fetch(this.graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.accessToken
          },
          body: JSON.stringify({ query, variables })
        });

        const data = await response.json();
        
        if (data.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        const cart = data.data.cart;
        
        if (cart?.deliveryGroups?.edges?.length > 0) {
          const deliveryOptions = cart.deliveryGroups.edges[0].node.deliveryOptions || [];
          
          if (deliveryOptions.length > 0) {
            // Transform to our expected format with 15% Art Emerge markup
            const SERVICE_FEE = 0.10; // 10%
            const BUFFER_FEE = 0.05; // 5%
            const TOTAL_MARKUP = SERVICE_FEE + BUFFER_FEE; // 15%

            return deliveryOptions.map(option => {
              const basePrice = parseFloat(option.estimatedCost.amount);
              const finalPrice = basePrice * (1 + TOTAL_MARKUP);

              return {
                id: option.handle,
                carrier: this.extractCarrierName(option.title),
                service: this.extractServiceName(option.title),
                price: Math.round(finalPrice * 100) / 100,
                est_days: this.estimateDeliveryDays(option.title),
                zone: 'Storefront API',
                currency: option.estimatedCost.currencyCode,
                metadata: {
                  original_price: basePrice,
                  art_emerge_fee: basePrice * SERVICE_FEE,
                  buffer_fee: basePrice * BUFFER_FEE,
                  shopify_title: option.title,
                  shopify_handle: option.handle,
                  is_real_shopify_rate: true,
                  source: 'storefront_cart_api'
                }
              };
            });
          }
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      }
      
      throw new Error('Shipping rates not available after maximum attempts');
      
    } catch (error) {
      console.error('Error getting cart shipping rates:', error);
      throw error;
    }
  }

  /**
   * Create a checkout with shipping address and line items (DEPRECATED - keeping for reference)
   */
  async createCheckoutWithShipping(shippingAddress, lineItems = []) {
    const mutation = `
      mutation checkoutCreate($input: CheckoutCreateInput!) {
        checkoutCreate(input: $input) {
          checkout {
            id
            webUrl
            shippingAddress {
              address1
              city
              province
              zip
              country
            }
            availableShippingRates {
              ready
              shippingRates {
                handle
                title
                price {
                  amount
                  currencyCode
                }
                priceV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
          checkoutUserErrors {
            field
            message
          }
        }
      }
    `;

    const input = {
      shippingAddress: {
        address1: shippingAddress.address1,
        address2: shippingAddress.address2 || '',
        city: shippingAddress.city,
        province: shippingAddress.province || shippingAddress.state,
        zip: shippingAddress.zip || shippingAddress.postal_code,
        country: shippingAddress.country || 'US',
        firstName: shippingAddress.firstName || 'Customer',
        lastName: shippingAddress.lastName || 'Name'
      }
    };

    // Only add lineItems if we have them
    if (lineItems && lineItems.length > 0) {
      input.lineItems = lineItems.map(item => ({
        variantId: item.variantId,
        quantity: item.quantity || 1
      }));
    }

    const variables = { input };

    try {
      const response = await fetch(this.graphqlEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query: mutation, variables })
      });

      const data = await response.json();
      
      if (data.errors) {
        throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
      }

      if (data.data.checkoutCreate.checkoutUserErrors.length > 0) {
        throw new Error(`Checkout errors: ${JSON.stringify(data.data.checkoutCreate.checkoutUserErrors)}`);
      }

      return data.data.checkoutCreate.checkout;
    } catch (error) {
      console.error('Error creating checkout with shipping:', error);
      throw error;
    }
  }

  /**
   * Query available shipping rates for a checkout
   */
  async getCheckoutShippingRates(checkoutId) {
    const query = `
      query getCheckout($id: ID!) {
        node(id: $id) {
          ... on Checkout {
            id
            availableShippingRates {
              ready
              shippingRates {
                handle
                title
                price {
                  amount
                  currencyCode
                }
                priceV2 {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;

    const variables = { id: checkoutId };

    try {
      // Poll for shipping rates (they may not be immediately available)
      let attempts = 0;
      const maxAttempts = 10;
      
      while (attempts < maxAttempts) {
        const response = await fetch(this.graphqlEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.accessToken
          },
          body: JSON.stringify({ query, variables })
        });

        const data = await response.json();
        
        if (data.errors) {
          throw new Error(`GraphQL errors: ${JSON.stringify(data.errors)}`);
        }

        const checkout = data.data.node;
        
        if (checkout?.availableShippingRates?.ready) {
          // Transform to our expected format with 15% Art Emerge markup
          const SERVICE_FEE = 0.10; // 10%
          const BUFFER_FEE = 0.05; // 5%
          const TOTAL_MARKUP = SERVICE_FEE + BUFFER_FEE; // 15%

          return checkout.availableShippingRates.shippingRates.map(rate => {
            const basePrice = parseFloat(rate.price.amount);
            const finalPrice = basePrice * (1 + TOTAL_MARKUP);

            return {
              id: rate.handle,
              carrier: this.extractCarrierName(rate.title),
              service: this.extractServiceName(rate.title),
              price: Math.round(finalPrice * 100) / 100,
              est_days: this.estimateDeliveryDays(rate.title),
              zone: 'Storefront API',
              currency: rate.price.currencyCode,
              metadata: {
                original_price: basePrice,
                art_emerge_fee: basePrice * SERVICE_FEE,
                buffer_fee: basePrice * BUFFER_FEE,
                shopify_title: rate.title,
                shopify_handle: rate.handle,
                is_real_shopify_rate: true,
                source: 'storefront_api'
              }
            };
          });
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, 1000));
        attempts++;
      }
      
      throw new Error('Shipping rates not available after maximum attempts');
      
    } catch (error) {
      console.error('Error getting checkout shipping rates:', error);
      throw error;
    }
  }

  /**
   * Extract carrier name from Shopify shipping rate title
   */
  extractCarrierName(title) {
    const carriers = {
      'ups': 'UPS',
      'fedex': 'FedEx', 
      'usps': 'USPS',
      'dhl': 'DHL',
      'canada post': 'Canada Post'
    };
    
    const titleLower = title.toLowerCase();
    for (const [key, value] of Object.entries(carriers)) {
      if (titleLower.includes(key)) {
        return value;
      }
    }
    
    return 'Shipping'; // Default fallback
  }

  /**
   * Extract service name from Shopify shipping rate title
   */
  extractServiceName(title) {
    // Remove common carrier prefixes to get service name
    const cleaned = title.replace(/^(UPS|FedEx|USPS|DHL|Canada Post)\s*/i, '');
    return cleaned || 'Standard';
  }

  /**
   * Estimate delivery days based on service title
   */
  estimateDeliveryDays(title) {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('overnight') || titleLower.includes('next day') || titleLower.includes('express')) {
      return 1;
    } else if (titleLower.includes('2 day') || titleLower.includes('priority')) {
      return 2;
    } else if (titleLower.includes('3 day')) {
      return 3;
    } else if (titleLower.includes('expedited')) {
      return 3;
    } else if (titleLower.includes('ground') || titleLower.includes('standard')) {
      return 5;
    } else {
      return 5; // Default
    }
  }
}

export const shopifyService = new ShopifyService(); 